import { getInstance } from '@/api/service'
import { getDeviceDetailsFromDevice, type DeviceDetails } from '@/helpers/devices/stateHelper'
import type { AveragePM, ChangeOverTime, CurrentReadings, StdTelemetry } from '@/types/devices'
import type { Study } from '@/types/studies'

export const DEVICE_UPDATE_INTERVAL_MS: number = 60000
export const THROTTLE_INTERVAL: number = 5000

export interface TelemetryResult {
  readings?: StdTelemetry
  timestamp?: string
  lastTelemetryStudyId?: string
}

export interface StreamReturnObject {
  stream?: ReadableStreamDefaultReader<Uint8Array>
}

export const getTelemetryAverages = async (deviceId: string): Promise<AveragePM> => {
  let averages: AveragePM
  const api = getInstance()

  try {
    const resp = await api.getTelemetryAverages(deviceId)
    if (resp.ok) {
      const body = await resp.json()
      if (body) {
        averages = body
      }
    }
  } catch {
    // ignore, returns undefined averages
  }

  return averages
}

export const getPercentChanged = async (deviceId: string): Promise<ChangeOverTime> => {
  let percentChanged: ChangeOverTime
  const api = getInstance()

  try {
    const pctResp = await api.getTelemetryPercentChanged(deviceId)
    if (pctResp.ok) {
      const body = await pctResp.json()
      if (body) {
        percentChanged = body
      }
    }
  } catch {
    // ignore, returns undefined percentChanged
  }

  return percentChanged
}

export const getLatestTelemetry = async (deviceId: string): Promise<TelemetryResult> => {
  const telemetry: TelemetryResult = {}
  const api = getInstance()
  try {
    const resp = await api.getTelemetryLatest(deviceId)
    if (resp.ok) {
      const body = await resp.json()
      if (body) {
        telemetry.readings = body
        telemetry.timestamp = body.timestamp
        telemetry.lastTelemetryStudyId = body.study_id
      }
    }
  } catch {
    // ignore, returns empty telemetry
  }

  return telemetry
}

export const getLatestCurrentReadings = async (
  deviceId: string
): Promise<CurrentReadings | undefined> => {
  const api = getInstance()
  try {
    const resp = await api.getCurrentReadingsLatest(deviceId)
    if (resp.ok) {
      const body = await resp.json()
      return body
    }
  } catch {
    // ignore, returns empty object
  }

  return undefined
}

export const getLatestStudy = async (
  deviceId: string,
  includeChildren: boolean,
  includeOnlyLatestTelemetry: boolean
): Promise<Study | null> => {
  let study: Study | null = null
  const api = getInstance()
  try {
    const resp = await api.getLatestDeviceStudy(
      deviceId,
      includeChildren,
      includeOnlyLatestTelemetry
    )
    if (resp.ok) {
      study = await resp.json()
    }
  } catch {
    // ignore, return empty study
  }

  return study
}

export const getDeviceDetails = async (deviceId: string): Promise<DeviceDetails> => {
  let detail: DeviceDetails = {}
  const api = getInstance()

  try {
    const resp = await api.getDevice(deviceId)
    if (resp.ok) {
      const body = await resp.json()
      detail = getDeviceDetailsFromDevice(body)
    }
  } catch {
    // ignore, returns empty detail
  }

  return detail
}

// streamLatestTelemetry makes a call to the devices API to open a SSE stream of latest telemetry
// the handler function is defined by the caller to process each reading
export const streamLatestTelemetry = async (
  deviceId: string,
  handler: (data: unknown) => void
): Promise<StreamReturnObject> => {
  const api = getInstance()
  return startStream(() => api.streamTelemetryLatest(deviceId), handler)
}

// streamCurrentReadings makes a call to the devices API to open a SSE stream of current readings
// the handler function is defined by the caller to process each reading
export const streamCurrentReadings = async (
  deviceId: string,
  handler: (data: unknown) => void
): Promise<StreamReturnObject> => {
  const api = getInstance()
  return startStream(() => api.streamCurrentReadings(deviceId), handler)
}

// streamDeviceState makes a call to the devices API to open a SSE stream of the device's state
// the handler function is defined by the caller to process each state update
export const streamDeviceState = async (
  deviceId: string,
  handler: (data: unknown) => void
): Promise<StreamReturnObject> => {
  const api = getInstance()
  return startStream(() => api.streamDeviceState(deviceId), handler)
}

// Reusable startStream helper
export const startStream = async (
  getResponseCallback: () => Promise<Response>,
  handler: (data: unknown) => void,
  returnObject: StreamReturnObject = {}
): Promise<StreamReturnObject> => {
  if (typeof getResponseCallback !== 'function') {
    throw new Error('getResponseCallback must be a function')
  }
  try {
    // make an api call to get the stream
    const response = await getResponseCallback()
    if (!response.ok) {
      throw new Error('Failed to fetch stream')
    }

    // get the readable stream from the response body and start pumping it
    const stream = response.body.getReader()
    pump(stream, handler)

    // returnObject is used to pass the stream back to the caller
    // this lets us restart the stream if it closes by setting a new
    // stream value because the object is passed by reference
    returnObject.stream = stream

    // set up stream to automatically restart if it is closed
    stream?.closed.catch(async () => {
      await startStream(getResponseCallback, handler, returnObject)
    })

    return returnObject
  } catch {
    // return empty object if the stream could not be started
    return {}
  }
}

// Helper to process the ReadableStream and call a handler for each chunk
export const pump = async function (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  handler: (data: unknown) => void,
  buffer = ''
): Promise<void> {
  const textDecoder = new TextDecoder()

  if (typeof handler !== 'function') {
    return
  }

  try {
    const { done, value } = await reader.read()
    if (done) {
      return
    }

    // Append the decoded chunk to the buffer
    buffer += textDecoder.decode(value, { stream: true })

    // Split the buffer into messages using the delimiter
    const splitMessages = buffer.split('\n\n')
    buffer = splitMessages.pop()! // Keep the last (possibly incomplete) part in the buffer

    // Process each complete message
    for (const message of splitMessages) {
      const trimmedEventData = message.replace(/^data: /, '')
      const data = JSON.parse(trimmedEventData)
      handler(data)
    }

    // get next value
    await pump(reader, handler, buffer)
  } catch {
    // we can swallow this error. It likely occurs after stream is closed
    // which we handle in streamLatestTelemetry
  }
}
